import React, {useState, useCallback, useEffect, useRef} from 'react'
import "./FlightInfoForm.css"
import { Row, Col } from 'react-bootstrap'
import axios from 'axios'
import infoIcon from "../../../../assets/images/flights/fluent_info-24-regular.svg"
import ticketIcon from "../../../../assets/images/flights/heroicons_ticket.svg"
import takeoffIcon from "../../../../assets/images/flights/take.svg"
import dropIcon from "../../../../assets/images/flights/drop.svg"
import timeIcon from "../../../../assets/images/flights/formkit_time.svg"
import handIcon from "../../../../assets/images/flights/fluent_money-hand-16-regular.svg"
import { PassengerForm, BillingSummary } from '../../index'
import { parsePhoneNumber } from 'react-phone-number-input';

const FlightInfoForm = ({ fligtData, onBack }) => {
    const [activeStep, setActiveStep] = useState(0); 
    const [passengerCount, setPassengerCount] = useState(1);
    const [passengerFormsData, setPassengerFormsData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [locationCache, setLocationCache] = useState({
        countries: [],
        cities: []
    });

    const steps = [
        { 
            title: "Fill In Your Info",
            component: Step1 
        },
        { 
            title: "Finalize Your Payment",
            component: Step2 
        },
        { 
            title: "Finish Ticketing",
            component: Step3 
        },
    ];

    const passengerFormsDataRef = useRef({});

    // Update both state and ref when data changes
    const updatePassengerData = useCallback((index, formData) => {
        const key = `Pax${index + 1}`;
        
        setPassengerFormsData(prev => {
            const newData = {
                ...prev,
                [key]: formData
            };
            // Also update the ref
            passengerFormsDataRef.current = newData;
            return newData;
        });
    }, []);

    const fetchLocations = useCallback(async (searchTerm = '') => {
    const url = searchTerm
        ? `https://crm.alltimestravels.com/api/method/alltimes.www.api.locations?search=${encodeURIComponent(searchTerm)}`
        : 'https://crm.alltimestravels.com/api/method/alltimes.www.api.locations';

    const response = await fetch(url);
    const data = await response.json();

    if (data.status_code === 200 && data.result.locations) {
        const locations = data.result.locations;
        const countries = [];
        const cities = [];

        const countryMap = new Map();
        locations.forEach((loc) => {
        if (loc.country && loc.country_code) {
            countryMap.set(loc.country_code, { name: loc.country, code: loc.country_code });
        }
        if (loc.location_name) {
            cities.push({
            name: loc.location_name,
            code: loc.location_code || loc.location_name,
            countryCode: loc.country_code,
            countryName: loc.country,
            });
        }
        });

        setLocationCache({
        countries: Array.from(countryMap.values()),
        cities,
        });
    }
    }, []);

    useEffect(() => {
        fetchLocations("");
    }, [fetchLocations]);

    const handlePassengerFormChange = (index, formData) => {
        setPassengerFormsData(prev => ({
            ...prev,
            [`Pax${index + 1}`]: formData
        }));
    };

    const handleAddPassenger = () => {
        setPassengerCount(prevCount => prevCount + 1);
    };

    const handleRemovePassenger = (index) => {
        if (passengerCount > 1) {
            setPassengerCount(prevCount => prevCount - 1);
            
            // Remove the passenger data
            setPassengerFormsData(prev => {
                const newData = {...prev};
                
                // Reindex remaining passengers
                const updatedData = {};
                let newIndex = 1;
                Object.keys(newData).forEach(key => {
                    if (key !== `Pax${index + 1}`) {
                        updatedData[`Pax${newIndex}`] = newData[key];
                        newIndex++;
                    }
                });
                
                return updatedData;
            });
        }
    };

    const renderPassengerForms = () => {
        const forms = [];
        for (let i = 0; i < passengerCount; i++) {
            forms.push(
            <PassengerForm 
                 key={i}
                index={i}
                formData={passengerFormsData[`Pax${i + 1}`] || {}}
                onRemove={() => handleRemovePassenger(i)}
                canRemove={passengerCount > 1}
                onFormChange={updatePassengerData}
                locationCache={locationCache}
                fetchLocations={fetchLocations}
            />
            );
        }
        return forms;
    };

    // Format the data for API
    // Format the data for API
    // Format the data for API
    const formatPassengerData = () => {
        const formattedPassengers = {};
        const currentData = passengerFormsDataRef.current;

        Object.keys(currentData).forEach((key, index) => {
            const passenger = currentData[key];
            
            // Format dates to YYYY-MM-DD
            const formatDate = (dateString) => {
                if (!dateString) return '';
                try {
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return ''; // Invalid date
                    return date.toISOString().split('T')[0]; // YYYY-MM-DD
                } catch (error) {
                    console.error('Date formatting error:', error);
                    return '';
                }
            };

          // Format phone number - extract country code and number correctly
            const formatPhoneData = (phoneNumber) => {
                if (!phoneNumber) return { 
                    CountryDialingCode: "", 
                    PhoneNumber: "", 
                    Type: "Mobile" 
                };
                
                try {
                    // Import parsePhoneNumber from react-phone-number-input
                    // Make sure to import this at the top of your file:
                    // import { parsePhoneNumber } from 'react-phone-number-input';
                    
                    const phoneData = parsePhoneNumber(phoneNumber);
                    
                    if (phoneData && phoneData.countryCallingCode) {
                        return {
                            CountryDialingCode: `+${phoneData.countryCallingCode}`,
                            PhoneNumber: phoneData.nationalNumber,
                            Type: "Mobile"
                        };
                    } else {
                        // Fallback: try to extract manually if parsePhoneNumber fails
                        const countryCodeMatch = phoneNumber.match(/^(\+\d{1,4})/);
                        let countryDialingCode = "";
                        let phoneNumberOnly = phoneNumber;
                        
                        if (countryCodeMatch) {
                            countryDialingCode = countryCodeMatch[1];
                            phoneNumberOnly = phoneNumber.replace(countryCodeMatch[1], '').replace(/^0+/, ''); // Remove leading zeros
                        }
                        
                        return {
                            CountryDialingCode: countryDialingCode,
                            PhoneNumber: phoneNumberOnly,
                            Type: "Mobile"
                        };
                    }
                } catch (error) {
                    console.error('Phone number formatting error:', error);
                    // Final fallback
                    const countryCodeMatch = phoneNumber.match(/^(\+\d{1,4})/);
                    let countryDialingCode = "";
                    let phoneNumberOnly = phoneNumber;
                    
                    if (countryCodeMatch) {
                        countryDialingCode = countryCodeMatch[1];
                        phoneNumberOnly = phoneNumber.replace(countryCodeMatch[1], '').replace(/^0+/, '');
                    }
                    
                    return {
                        CountryDialingCode: countryDialingCode,
                        PhoneNumber: phoneNumberOnly,
                        Type: "Mobile"
                    };
                }
            };

            // Format title to uppercase
            const formatTitle = (title) => {
                const titleMap = {
                    'Mr': 'MR',
                    'Mrs': 'MRS', 
                    'Ms': 'MS',
                    'Madam': 'MADAM',
                    'Miss': 'MISS',
                    'Master': 'MASTER',
                    'Mx': 'MX',
                    'Dr': 'DR',
                    'Prof': 'PROF'
                };
                return titleMap[title] || 'MR';
            };

            // Format gender to lowercase
            const formatGender = (gender) => {
                return gender ? gender.toLowerCase() : "male";
            };

            // Get phone data
            const phoneData = formatPhoneData(passenger.phoneNumber);

            formattedPassengers[key] = {
                PassengerTypeCode: passenger.passengerType || "ADT",
                Title: formatTitle(passenger.title),
                Gender: formatGender(passenger.gender),
                Name: {
                    First: passenger.firstName || "",
                    Middle: passenger.middleName || "",
                    Last: passenger.lastName || ""
                },
                BirthDate: formatDate(passenger.date_of_birth),
                NationalityCountryCode: passenger.nationality || "",
                ResidenceCountryCode: passenger.residenceCountry || "",
                TravelDocument: {
                    DocumentNumber: passenger.documentNumber || "",
                    DocumentType: passenger.documentType || "Passport",
                    Name: {
                        First: passenger.firstName || "",
                        Middle: passenger.middleName || "",
                        Last: passenger.lastName || ""
                    },
                    Gender: formatGender(passenger.gender),
                    BirthDate: formatDate(passenger.date_of_birth),
                    ExpirationDate: formatDate(passenger.expirationDate),
                    IssuanceDate: formatDate(passenger.issuanceDate),
                    BirthCountryCode: passenger.birthCountry || "",
                    NationalityCountryCode: passenger.nationality || "",
                    IssuanceCountryCode: passenger.issuanceCountry || ""
                },
                Contact: {
                    Email: passenger.email || "",
                    Phone: phoneData,
                    Address: {
                        CountryCode: passenger.addressCountry || "",
                        CityCode: passenger.phoneNumber ? passenger.phoneNumber.replace('+', '') : "", // Full phone number without '+'
                        Line1: passenger.addressLine1 || "",
                        Line2: passenger.addressLine2 || ""
                    }
                }
            };
        });

        return {
            OfferId: fligtData?.offerData?.offerId || "your-offer-id-here",
            Passengers: formattedPassengers
        };
    };
    // Handle next step from billing summary
    const handleBillingNext = async () => {
        if (activeStep === 0) {
            // Debug: log the current form data
            
            const currentData = passengerFormsDataRef.current;
            // Validate required fields before proceeding
            const requiredFieldsValid = validateRequiredFields(currentData);
            
            if (!requiredFieldsValid) {
                
                
                // More detailed validation feedback
                const validationErrors = getDetailedValidationErrors();
           
                
                alert("Please fill in all required fields for all passengers.");
                return;
            }

            setIsLoading(true);
            try {
                const formattedData = formatPassengerData();
               

                // Make API call
                const response = await axios.post(
                    "https://crm.alltimestravels.com/api/method/alltimes.www.flight_api.add_passengers_to_offer",
                    formattedData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );

             
                
                if (response.data.success) {
                    setActiveStep(activeStep + 1);
                } else {
                    alert("Failed to add passengers: " + (response.data.message || "Unknown error"));
                }
            } catch (error) {
                console.error("API Error:", error);
                alert("Error submitting passenger data. Please try again.");
            } finally {
                setIsLoading(false);
            }
        } else {
            setActiveStep(activeStep + 1);
        }   
    };

    // Helper function to get detailed validation errors
    const getDetailedValidationErrors = () => {
        const requiredFields = [
            'passengerType', 'title', 'gender', 'firstName', 'lastName', 
            'date_of_birth', 'nationality', 'residenceCountry',
            'documentNumber', 'documentType', 'expirationDate'
        ];

        const errors = {};

        Object.keys(passengerFormsData).forEach((passengerKey, passengerIndex) => {
            const passenger = passengerFormsData[passengerKey];
            errors[passengerKey] = [];

            requiredFields.forEach(field => {
                const value = passenger[field];
                let isValid = true;

                if (value === null || value === undefined || value === '') {
                    isValid = false;
                } else if (typeof value === 'string' && value.trim() === '') {
                    isValid = false;
                }

                if (!isValid) {
                    errors[passengerKey].push(field);
                }
            });
        });

        return errors;
    };


    useEffect(() => {
        console.log("PassengerFormsData updated:", passengerFormsData);
    }, [passengerFormsData]);

    useEffect(() => {
        console.log("PassengerFormsDataRef updated:", passengerFormsDataRef.current);
    }, [passengerFormsDataRef.current]);
    // Validate required fields
    // Validate required fields using the ref (which has the latest data)
    // Validate required fields using the ref (which has the latest data)
const validateRequiredFields = () => {
    const requiredFields = [
        'passengerType', 'title', 'gender', 'firstName', 'lastName', 
        'date_of_birth', 'nationality', 'residenceCountry',
        'documentNumber', 'documentType', 'expirationDate'
    ];

    const currentData = passengerFormsDataRef.current;
    
    // Check if we have any passenger data
    if (Object.keys(currentData).length === 0) {
        console.log("No passenger data found");
        return false;
    }

    // Check if we have the right number of passengers
    if (Object.keys(currentData).length !== passengerCount) {
        console.log("Passenger count mismatch");
        return false;
    }

    const allValid = Object.values(currentData).every((passenger, index) => {
        const passengerKey = Object.keys(currentData)[index];
        const isValid = requiredFields.every(field => {
            const value = passenger[field];
            
            // Check if value exists and is not empty
            if (value === null || value === undefined || value === '') {
                console.log(`Missing field: ${field} for passenger ${passengerKey}`, value);
                return false;
            }
            
            // If it's a string, check if it's not just whitespace
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed === '') {
                    console.log(`Empty string field: ${field} for passenger ${passengerKey}`);
                    return false;
                }
                return true;
            }
            
            // For dates, check if it's a valid date
            if (field.includes('date') || field.includes('Date')) {
                if (!value || new Date(value).toString() === 'Invalid Date') {
                    console.log(`Invalid date field: ${field} for passenger ${passengerKey}`, value);
                    return false;
                }
            }
            
            return true;
        });
        
        if (!isValid) {
            console.log(`Passenger ${passengerKey} is invalid`);
        }
        
        return isValid;
    });

    console.log("Validation result:", allValid);
    return allValid;
};

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handlePrevious = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    // Step Components
    function Step1() {
        return (
            <Col lg={9}>
                <div className='flight-info-details'>
                    <div className='flight-popup-categories'>
                        {fligtData?.bundleData && (
                            <div className='flight-popup-category'>
                                <img src={ticketIcon} alt="Ticket icon" />
                                <div>Have bundles</div>
                            </div>
                        )}
                        {fligtData?.offerData?.canBeHeld && (
                            <div className='flight-popup-category'>
                                <img src={timeIcon} alt="Time icon" />
                                <div>Can Hold</div>
                            </div>
                        )}
                        {fligtData?.offerData?.refundability !== "UnKnown" && (
                            <div className='flight-popup-category'>
                                <img src={handIcon} alt="Hand icon" />
                                <div>Refundable</div>
                            </div>
                        )}
                    </div>
                    {fligtData?.journeysData?.map((item, index) => {
                        const departureDate = new Date(item.departureDateTime);
                        const arrivalDate = new Date(item.arrivalDateTime);

                        const formatTime = (date) => {
                            return date.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });
                        };

                        const formatDate = (date) => {
                            return date.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            });
                        };

                        const formatDuration = (minutes) => {
                            const hours = Math.floor(minutes / 60);
                            const mins = minutes % 60;
                            return `${hours}H ${mins}Min`;
                        };

                        return (
                         
                            <div className='flight-popup-details' key={index}>
                                <div className='flight-popup-departure'>
                                    <div className='flight-popup-details-header'>
                                        {item.origin} <span>{item.departureTerminal}</span>
                                    </div>
                                    <div className='flight-popup-details-time-container'>
                                        <div className='flight-popup-details-time-icon'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                                                <path d="M0.667011 6.00006L6.00034 0.666731M6.00034 0.666731V5.78673M6.00034 0.666731L0.880345 0.666731" stroke="#2D343F" strokeWidth="0.615385" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className='flight-popup-details-time'>{formatTime(departureDate)}</div>
                                    </div>
                                    <div className='flight-popup-details-date'>{formatDate(departureDate)}</div>
                                </div>
                                <div className='flight-popup-details-info'>
                                    <div className='flight-popup-details-info-data'>
                                        {item.flightTimeInMinutes ? formatDuration(item.flightTimeInMinutes) : 'N/A'}
                                    </div>
                                </div>
                                <div className='flight-popup-destination'>
                                    <div className='flight-popup-details-header'>
                                        {item.destination} <span>{item.arrivalTerminal}</span>
                                    </div>
                                    <div className='flight-popup-details-time-container'>
                                        <div className='flight-popup-details-time-icon'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                                                <path d="M0.666523 0.666684L5.99986 6.00002M5.99986 6.00002V0.880017M5.99986 6.00002H0.879856" stroke="#2D343F" strokeWidth="0.615385" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className='flight-popup-details-time'>{formatTime(arrivalDate)}</div>
                                    </div>
                                    <div className='flight-popup-details-date'>{formatDate(arrivalDate)}</div>
                                </div>
                            </div>
                        
                        );
                    })}
                </div>

                <div className='flight-info-desc-container'>
                    <div className='flight-info-desc-title'>Who's Traveling?</div> 
                    <div className='flight-info-desc'>
                        <h1>Given names<span>*</span> - Last name (surname)<span>*</span> - Gender on ID<span>*</span> - Date of birth<span>*</span> - Nationality (country/region)<span>*</span></h1>
                        <div>Please enter the name exactly as it appears on your travel documents for check-in.</div>
                        <div>If the name is incorrect, you may not be able to board your flight and a cancellation fee will be charged.</div>
                        <div>To ensure your trip goes smoothly, please make sure that the passenger's travel document is valid for at least 6 months from the date the trip ends.</div>
                    </div>      
                </div>

                {renderPassengerForms()}
                <div className='add-passenger-btn' onClick={handleAddPassenger}>
                    + Add other passenger
                </div>
            </Col>
        );
    }

    function Step2() {
        return (
            <Col lg={9}>
                <div className='payment-step'>
                    <h2>Finalize Your Payment</h2>
                    <div className='payment-content'>
                        {/* Add your payment form components here */}
                        <p>Payment form will go here...</p>
                    </div>
                </div>
            </Col>
        );
    }

    function Step3() {
        return (
            <Col lg={9}>
                <div className='confirmation-step'>
                    <h2>Finish Ticketing</h2>
                    <div className='confirmation-content'>
                        {/* Add your confirmation components here */}
                        <p>Booking confirmation will go here...</p>
                    </div>
                </div>
            </Col>
        );
    }

    const CurrentStepComponent = steps[activeStep].component;

    return (
        <div>
            {/* Step Navigation */}
            <Row>
                <Col lg={9}>
                    <div className="step-navigation">
                        {steps.map((step, index) => (
                            <div 
                                key={index}
                                className={`step-item ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}
                            >
                                <div className="step-number">{index + 1}</div>
                                <div className="step-title">{step.title}</div>
                            </div>
                            
                        ))}
                    </div>
                </Col>
                <Col lg={3}></Col>
            </Row>
           
            <Row>
                <CurrentStepComponent />
                <div className="col-lg-3">
                    <BillingSummary 
                        refundability={true} 
                        onNext={handleBillingNext}
                        isLoading={isLoading && activeStep === 0}
                        currentStep={activeStep}
                    />
                </div>
            </Row>
        </div>
    )
}

export default FlightInfoForm